import React, { useEffect, useRef, useState } from "react";
import { Plus, Send, Scissors, SlidersHorizontal, Sliders, Type, X } from "lucide-react";
import ReactCrop, {
    convertToPixelCrop,
    PercentCrop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import "./chat-image-preview.css";

interface FilePreview {
    file: File;
    src: string;
}

interface Props {
    files: FilePreview[];
    caption: string;
    isViewOnce?: boolean;
    onChangeCaption: (v: string) => void;
    onToggleViewOnce?: () => void;
    onConfirm: (editedFiles: FilePreview[], isViewOnce?: boolean) => void;
    onClose: () => void;
    onRemove: (index: number) => void;
    onAddMore: (newFiles: FilePreview[]) => void;
}

// ─── Filters ──────────────────────────────────────────────────────────────────
interface FilterDef { id: string; label: string; css: string; swatch: string; }
const FILTERS: FilterDef[] = [
    { id: "normal",  label: "Normal",  css: "none",                                              swatch: "#555"    },
    { id: "lavanda", label: "Lavanda", css: "hue-rotate(270deg) saturate(1.4)",                   swatch: "#b3a4e0" },
    { id: "quente",  label: "Quente",  css: "sepia(0.5) saturate(1.3) hue-rotate(-15deg)",        swatch: "#e0c4a4" },
    { id: "frio",    label: "Frio",    css: "saturate(0.8) hue-rotate(200deg) brightness(1.05)",  swatch: "#a0bce0" },
    { id: "pb",      label: "P&B",     css: "grayscale(1)",                                       swatch: "#888"    },
    { id: "vintage", label: "Vintage", css: "sepia(0.8) contrast(0.9) brightness(0.9)",           swatch: "#c4a882" },
    { id: "neon",    label: "Neon",    css: "saturate(2.5) contrast(1.2) brightness(1.1)",        swatch: "#3d1a78" },
];

interface Adjustments { brightness: number; contrast: number; saturation: number; }
const DEFAULT_ADJ: Adjustments = { brightness: 100, contrast: 100, saturation: 100 };
const TEXT_COLORS = ["#ffffff", "#f87171", "#60a5fa", "#34d399", "#fbbf24", "#e879f9"];

type ActiveTool = "crop" | "filtros" | "ajustes" | "texto" | null;

const adjToCSS = (adj: Adjustments, filterCss: string) => {
    const base = `brightness(${adj.brightness}%) contrast(${adj.contrast}%) saturate(${adj.saturation}%)`;
    return filterCss === "none" ? base : `${base} ${filterCss}`;
};

// Per-file edit state
interface FileEditState {
    filter: FilterDef;
    adj: Adjustments;
    textValue: string;
    textColor: string;
    textBold: boolean;
    crop: PercentCrop | undefined;
    cropped: boolean;
    /** Data URL of the cropped region for live preview outside crop mode */
    cropPreviewSrc: string;
}
const defaultEditState = (): FileEditState => ({
    filter: FILTERS[0],
    adj: DEFAULT_ADJ,
    textValue: "",
    textColor: TEXT_COLORS[0],
    textBold: false,
    crop: undefined,
    cropped: false,
    cropPreviewSrc: "",
});

const ChatImagePreview: React.FC<Props> = ({
    files,
    caption,
    isViewOnce = false,
    onChangeCaption,
    onToggleViewOnce,
    onConfirm,
    onClose,
    onRemove,
    onAddMore,
}) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [activeTool, setActiveTool] = useState<ActiveTool>(null);
    const [editStates, setEditStates] = useState<FileEditState[]>(() => files.map(defaultEditState));

    const addInputRef = useRef<HTMLInputElement>(null);
    const captionRef = useRef<HTMLInputElement>(null);
    const imgRef = useRef<HTMLImageElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    // Sync editStates when files change
    useEffect(() => {
        setEditStates(prev => {
            const next = [...prev];
            while (next.length < files.length) next.push(defaultEditState());
            return next.slice(0, files.length);
        });
    }, [files.length]);

    // Keep activeIndex in bounds when files change
    useEffect(() => {
        if (activeIndex >= files.length) setActiveIndex(Math.max(0, files.length - 1));
    }, [files.length]);

    // Focus caption on open
    useEffect(() => {
        setTimeout(() => captionRef.current?.focus(), 80);
    }, []);

    const active = files[activeIndex];
    const editState = editStates[activeIndex] ?? defaultEditState();

    if (!active) return null;

    const isImage = active.file.type.startsWith("image");
    const isVideo = active.file.type.startsWith("video");
    const canEdit = isImage;

    // ── Edit state updater ───────────────────────────────────────────────────
    const updateEdit = (patch: Partial<FileEditState>) => {
        setEditStates(prev => {
            const next = [...prev];
            next[activeIndex] = { ...(next[activeIndex] ?? defaultEditState()), ...patch };
            return next;
        });
    };

    // ── Crop ─────────────────────────────────────────────────────────────────
    const onImageLoad = () => {
        // Only initialise a default crop selection when the crop tool is active
        // and no crop has been set yet. Skip when showing the cropped preview.
        if (activeTool === "crop" && !editState.crop) {
            const c = { unit: "%" as const, width: 100, height: 100, x: 0, y: 0 };
            updateEdit({ crop: c as PercentCrop });
        }
    };

    /** Render the current crop selection to a data URL and store it for preview */
    const commitCropPreview = (completedCrop: PercentCrop) => {
        const img = imgRef.current;
        const canvas = canvasRef.current;
        if (!img || !canvas) return;

        const pixelCrop = convertToPixelCrop(completedCrop, img.naturalWidth, img.naturalHeight);
        canvas.width = Math.round(pixelCrop.width);
        canvas.height = Math.round(pixelCrop.height);
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.drawImage(
            img,
            pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
            0, 0, canvas.width, canvas.height,
        );
        const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
        updateEdit({ crop: completedCrop, cropped: true, cropPreviewSrc: dataUrl });
    };

    // ── Toolbar toggle ───────────────────────────────────────────────────────
    const toggleTool = (tool: ActiveTool) => {
        setActiveTool(prev => prev === tool ? null : tool);
    };

    // ── Add more files ───────────────────────────────────────────────────────
    const handleAddInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newFiles = Array.from(e.target.files ?? [])
            .slice(0, 10 - files.length)
            .map(f => ({ file: f, src: URL.createObjectURL(f) }));
        if (newFiles.length) onAddMore(newFiles);
        if (addInputRef.current) addInputRef.current.value = "";
    };

    // ── Drag-and-drop onto the overlay ────────────────────────────────────────
    const handleOverlayDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const droppedFiles = Array.from(e.dataTransfer.files)
            .filter(f => f.type.startsWith("image/") || f.type.startsWith("video/"))
            .slice(0, 10 - files.length);
        if (droppedFiles.length === 0) return;

        const newPreviews = droppedFiles.map(f => ({
            file: f,
            src: URL.createObjectURL(f),
        }));
        onAddMore(newPreviews);
    };

    // ── Apply edits to a single image file ───────────────────────────────────
    const applyEditsToFile = async (
        src: string,
        origFile: File,
        es: FileEditState,
        canvas: HTMLCanvasElement,
    ): Promise<FilePreview> => {
        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
            const i = new Image();
            i.onload = () => resolve(i);
            i.onerror = reject;
            i.src = src;
        });

        // Draw to canvas (applying crop if set)
        if (es.crop && es.cropped) {
            // Use a temporary visible-size canvas matching the displayed image
            // We need pixel crop based on natural size; use img dimensions directly
            const pixelCrop = convertToPixelCrop(
                es.crop,
                img.naturalWidth,
                img.naturalHeight,
            );
            canvas.width = Math.round(pixelCrop.width);
            canvas.height = Math.round(pixelCrop.height);
            const ctx2 = canvas.getContext("2d")!;
            ctx2.drawImage(
                img,
                pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
                0, 0, canvas.width, canvas.height,
            );
        } else {
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            canvas.getContext("2d")!.drawImage(img, 0, 0);
        }

        // Apply filter + adjustments
        const hasEdits =
            es.filter.css !== "none" ||
            es.adj.brightness !== 100 ||
            es.adj.contrast !== 100 ||
            es.adj.saturation !== 100 ||
            es.textValue.trim() !== "" ||
            (es.crop && es.cropped);

        if (!hasEdits) {
            return { file: origFile, src };
        }

        const off = document.createElement("canvas");
        off.width = canvas.width;
        off.height = canvas.height;
        const ctx = off.getContext("2d")!;
        ctx.filter = adjToCSS(es.adj, es.filter.css);
        ctx.drawImage(canvas, 0, 0);

        // Text overlay
        if (es.textValue.trim()) {
            ctx.filter = "none";
            ctx.font = `${es.textBold ? "700" : "400"} ${Math.round(off.width * 0.06)}px sans-serif`;
            ctx.fillStyle = es.textColor;
            ctx.textAlign = "center";
            ctx.shadowColor = "rgba(0,0,0,0.6)";
            ctx.shadowBlur = 6;
            ctx.fillText(es.textValue, off.width / 2, off.height * 0.88);
        }

        const blob = await new Promise<Blob>((res) =>
            off.toBlob((b) => res(b!), "image/jpeg", 0.92)
        );
        const editedFile = new File(
            [blob],
            origFile.name.replace(/\.[^.]+$/, ".jpg"),
            { type: "image/jpeg" },
        );
        return { file: editedFile, src: URL.createObjectURL(editedFile) };
    };

    // ── Confirm: apply edits to ALL files then send ───────────────────────────
    const handleConfirm = async () => {
        const canvas = canvasRef.current;
        if (!canvas) { onConfirm(files, isViewOnce); return; }

        try {
            // Sequential to avoid sharing the canvas across concurrent calls
            const editedFiles: FilePreview[] = [];
            for (let i = 0; i < files.length; i++) {
                const fp = files[i];
                const es = editStates[i] ?? defaultEditState();
                if (!fp.file.type.startsWith("image")) {
                    editedFiles.push(fp);
                } else {
                    editedFiles.push(await applyEditsToFile(fp.src, fp.file, es, canvas));
                }
            }
            onConfirm(editedFiles, isViewOnce);
        } catch (_) {
            onConfirm(files, isViewOnce);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleConfirm(); }
        if (e.key === "Escape") {
            if (activeTool) setActiveTool(null);
            else onClose();
        }
    };

    const liveFilter = adjToCSS(editState.adj, editState.filter.css);
    const cropActive = activeTool === "crop";

    return (
        <div
            className="cip-overlay"
            onKeyDown={handleKeyDown}
            onDrop={handleOverlayDrop}
            onDragOver={e => e.preventDefault()}
        >
            {/* ── Header ── */}
            <div className="cip-header">
                <button className="cip-btn-icon" onClick={onClose} aria-label="Fechar">
                    <X size={22} />
                </button>
                <span className="cip-header-title">
                    {files.length === 1 ? "1 arquivo" : `${files.length} arquivos`}
                </span>

                {/* Tool buttons — only for images */}
                {canEdit && (
                    <div className="cip-toolbar">
                        {activeTool && (
                            <button
                                className="cip-tool-ok"
                                onClick={() => setActiveTool(null)}
                                type="button"
                                aria-label="OK"
                            >
                                OK
                            </button>
                        )}
                        <button
                            className={`cip-tool-btn${activeTool === "crop" ? " cip-tool-active" : ""}`}
                            onClick={() => toggleTool("crop")}
                            title="Cortar"
                            type="button"
                        >
                            <Scissors size={18} />
                        </button>
                        <button
                            className={`cip-tool-btn${activeTool === "filtros" ? " cip-tool-active" : ""}`}
                            onClick={() => toggleTool("filtros")}
                            title="Filtros"
                            type="button"
                        >
                            <Sliders size={18} />
                        </button>
                        <button
                            className={`cip-tool-btn${activeTool === "ajustes" ? " cip-tool-active" : ""}`}
                            onClick={() => toggleTool("ajustes")}
                            title="Ajustes"
                            type="button"
                        >
                            <SlidersHorizontal size={18} />
                        </button>
                        <button
                            className={`cip-tool-btn${activeTool === "texto" ? " cip-tool-active" : ""}`}
                            onClick={() => toggleTool("texto")}
                            title="Texto"
                            type="button"
                        >
                            <Type size={18} />
                        </button>
                    </div>
                )}

                {!canEdit && <div style={{ width: 38 }} />}
            </div>

            {/* ── Main preview ── */}
            <div className="cip-main">
                {isImage && (
                    cropActive ? (
                        <ReactCrop
                            crop={editState.crop}
                            onChange={(_, p) => updateEdit({ crop: p })}
                            onComplete={(_, p) => commitCropPreview(p)}
                            className="cip-react-crop"
                        >
                            <img
                                ref={imgRef}
                                key={active.src}
                                src={active.src}
                                alt="preview"
                                className="cip-preview-img"
                                draggable={false}
                                onLoad={onImageLoad}
                                style={{ filter: liveFilter }}
                            />
                        </ReactCrop>
                    ) : (
                        <div className="cip-img-wrap">
                            <img
                                ref={imgRef}
                                key={editState.cropPreviewSrc || active.src}
                                src={editState.cropPreviewSrc || active.src}
                                alt="preview"
                                className="cip-preview-img"
                                draggable={false}
                                onLoad={onImageLoad}
                                style={{ filter: liveFilter }}
                            />
                            {editState.textValue && (
                                <div
                                    className="cip-text-overlay"
                                    style={{
                                        color: editState.textColor,
                                        fontWeight: editState.textBold ? 700 : 400,
                                    }}
                                >
                                    {editState.textValue}
                                </div>
                            )}
                        </div>
                    )
                )}
                {isVideo && (
                    <video
                        key={active.src}
                        src={active.src}
                        className="cip-preview-img"
                        controls
                        autoPlay={false}
                    />
                )}
                {!isImage && !isVideo && (
                    <div className="cip-file-placeholder">
                        <span className="cip-file-name">{active.file.name}</span>
                    </div>
                )}
            </div>

            {/* ── Tool panels ── */}
            {activeTool && activeTool !== "crop" && (
                <div className="cip-tool-panel">
                    {/* Filtros */}
                    {activeTool === "filtros" && (
                        <div className="cip-filters-row">
                            {FILTERS.map(f => (
                                <button
                                    key={f.id}
                                    className={`cip-filter-item${editState.filter.id === f.id ? " cip-filter-active" : ""}`}
                                    onClick={() => updateEdit({ filter: f })}
                                    type="button"
                                >
                                    <div
                                        className="cip-filter-thumb"
                                        style={{
                                            backgroundImage: `url(${active.src})`,
                                            backgroundSize: "cover",
                                            backgroundPosition: "center",
                                            filter: f.css === "none" ? undefined : f.css,
                                        }}
                                    >
                                        {editState.filter.id === f.id && <span className="cip-filter-check">✓</span>}
                                    </div>
                                    <span className="cip-filter-label">{f.label}</span>
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Ajustes */}
                    {activeTool === "ajustes" && (
                        <div className="cip-adj-panel">
                            {([
                                { key: "brightness", label: "Brilho", min: 0, max: 200 },
                                { key: "contrast",   label: "Contraste", min: 0, max: 200 },
                                { key: "saturation", label: "Saturação", min: 0, max: 200 },
                            ] as { key: keyof Adjustments; label: string; min: number; max: number }[]).map(({ key, label, min, max }) => (
                                <div key={key} className="cip-adj-row">
                                    <span className="cip-adj-label">{label}</span>
                                    <input
                                        type="range" min={min} max={max}
                                        value={editState.adj[key]}
                                        className="cip-slider"
                                        onChange={e => updateEdit({ adj: { ...editState.adj, [key]: +e.target.value } })}
                                    />
                                    <span className="cip-adj-val">{editState.adj[key]}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Texto */}
                    {activeTool === "texto" && (
                        <div className="cip-text-panel">
                            <div className="cip-text-tools">
                                <button
                                    className={`cip-tool-btn${editState.textBold ? " cip-tool-active" : ""}`}
                                    onClick={() => updateEdit({ textBold: !editState.textBold })}
                                    type="button"
                                ><strong>B</strong></button>
                                <div className="cip-color-dots">
                                    {TEXT_COLORS.map(c => (
                                        <button
                                            key={c}
                                            className={`cip-color-dot${editState.textColor === c ? " cip-color-active" : ""}`}
                                            style={{ background: c }}
                                            onClick={() => updateEdit({ textColor: c })}
                                            type="button"
                                        />
                                    ))}
                                </div>
                            </div>
                            <input
                                className="cip-text-input"
                                placeholder="Digite um texto…"
                                value={editState.textValue}
                                onChange={e => updateEdit({ textValue: e.target.value })}
                                maxLength={80}
                                autoFocus
                            />
                        </div>
                    )}
                </div>
            )}

            {/* ── Bottom bar ── */}
            <div className="cip-bottom">
                {/* Thumbnail strip */}
                <div className="cip-strip">
                    {files.map((f, i) => (
                        <div
                            key={f.src}
                            className={`cip-thumb-wrap${i === activeIndex ? " cip-thumb-active" : ""}`}
                            onClick={() => { setActiveIndex(i); setActiveTool(null); }}
                        >
                            {f.file.type.startsWith("image") ? (
                                <img src={f.src} alt="" className="cip-thumb" draggable={false} />
                            ) : f.file.type.startsWith("video") ? (
                                <video src={f.src} className="cip-thumb" muted />
                            ) : (
                                <div className="cip-thumb cip-thumb-file">
                                    <span>{f.file.name.split(".").pop()?.toUpperCase()}</span>
                                </div>
                            )}
                            <button
                                className="cip-thumb-remove"
                                onClick={(e) => { e.stopPropagation(); onRemove(i); }}
                                aria-label="Remover"
                                type="button"
                            >
                                <X size={11} />
                            </button>
                        </div>
                    ))}

                    {files.length < 10 && (
                        <button
                            className="cip-thumb-add"
                            onClick={() => addInputRef.current?.click()}
                            aria-label="Adicionar mais"
                            type="button"
                        >
                            <Plus size={20} />
                        </button>
                    )}
                    <input
                        ref={addInputRef}
                        type="file"
                        multiple
                        accept="image/*,video/*,.pdf,.doc,.docx"
                        style={{ display: "none" }}
                        onChange={handleAddInput}
                    />
                </div>

                {/* Caption + send */}
                <div className="cip-caption-row">
                    <input
                        ref={captionRef}
                        className="cip-caption-input"
                        placeholder="Adicionar legenda…"
                        value={caption}
                        onChange={(e) => onChangeCaption(e.target.value)}
                        onKeyDown={handleKeyDown}
                    />
                    {onToggleViewOnce && files.some(f => f.file.type.startsWith("image")) && (
                        <button
                            className={`cip-view-once-btn ${isViewOnce ? "active" : ""}`}
                            onClick={onToggleViewOnce}
                            title={isViewOnce ? "Visualização única ativada" : "Visualização única"}
                            aria-label="Visualização única"
                            type="button"
                        >
                            <span className="cip-view-once-num">1</span>
                        </button>
                    )}
                    <button className="cip-send-btn" onClick={handleConfirm} aria-label="Enviar" type="button">
                        <Send size={20} />
                    </button>
                </div>
            </div>

            {/* Hidden canvas for export */}
            <canvas ref={canvasRef} className="d-none" />
        </div>
    );
};

export default ChatImagePreview;
